import { Head } from '@inertiajs/react';
import {
    ShieldExclamationIcon,
    ExclamationTriangleIcon,
    ServerStackIcon,
    ClockIcon,
    NoSymbolIcon,
    WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

const errorConfig = {
    403: {
        title: 'Access Denied',
        description: "You don't have permission to access this page. Please contact your administrator if you believe this is a mistake.",
        Icon: ShieldExclamationIcon,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100',
    },
    404: {
        title: 'Page Not Found',
        description: "The page you're looking for doesn't exist or has been moved. Please check the URL or navigate back.",
        Icon: ExclamationTriangleIcon,
        color: 'text-slate-600',
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        iconBg: 'bg-slate-100',
    },
    419: {
        title: 'Session Expired',
        description: 'Your session has expired. Please refresh the page and try again.',
        Icon: ClockIcon,
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        iconBg: 'bg-orange-100',
    },
    429: {
        title: 'Too Many Requests',
        description: "You've made too many requests in a short period. Please wait a moment and try again.",
        Icon: NoSymbolIcon,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        iconBg: 'bg-red-100',
    },
    500: {
        title: 'Server Error',
        description: 'Something went wrong on our end. Please try again later or contact support if the issue persists.',
        Icon: ServerStackIcon,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        iconBg: 'bg-red-100',
    },
    503: {
        title: 'Service Unavailable',
        description: 'The system is currently undergoing maintenance. Please check back shortly.',
        Icon: WrenchScrewdriverIcon,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        iconBg: 'bg-blue-100',
    },
};

const defaultError = {
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again or contact support.',
    Icon: ExclamationTriangleIcon,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    iconBg: 'bg-slate-100',
};

export default function Error({ status }) {
    const config = errorConfig[status] || defaultError;
    const { title, description, Icon, color, bg, border, iconBg } = config;

    return (
        <>
            <Head title={`${status} - ${title}`} />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex items-center justify-center p-6">
                <div className="w-full max-w-lg">
                    {/* Card */}
                    <div className={`${bg} ${border} border rounded-2xl shadow-sm overflow-hidden`}>
                        {/* Top accent bar */}
                        <div className="h-1 bg-emerald-700" />

                        <div className="px-8 py-10 text-center">
                            {/* Icon */}
                            <div className={`${iconBg} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                                <Icon className={`w-8 h-8 ${color}`} />
                            </div>

                            {/* Status code */}
                            <p className="text-emerald-700 font-bold text-sm tracking-widest uppercase mb-2">
                                Error {status}
                            </p>

                            {/* Title */}
                            <h1 className="text-2xl font-bold text-slate-800 mb-3">
                                {title}
                            </h1>

                            {/* Description */}
                            <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-sm mx-auto">
                                {description}
                            </p>

                            {/* Actions */}
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={() => window.history.back()}
                                    className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400"
                                >
                                    Go Back
                                </button>
                                <a
                                    href="/"
                                    className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600"
                                >
                                    Back to Dashboard
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Footer text */}
                    <p className="text-center text-xs text-slate-400 mt-6">
                        If this problem continues, please contact your system administrator.
                    </p>
                </div>
            </div>
        </>
    );
}
